import type { FragmentOf } from "@opensea/graphql"
import { Button } from "@opensea/next-ui-kit"
import { type ButtonProps, Tooltip } from "@opensea/ui-kit"
import type { Nullable } from "@opensea/utils"
import { isAddressEqual } from "@opensea/utils"
import type { ComponentProps } from "react"
import { TokenPrice } from "@/components/price/TokenPrice"
import type { TokenPriceFragment } from "@/components/price/TokenPrice.fragment"
import { useIsOwnProfileAddress } from "@/components/profile/ProfileAddressProvider"
import { useAddress } from "@/hooks/wallet"

type Props = {
  makerAddress: string
  pricePerItem: Nullable<FragmentOf<typeof TokenPriceFragment>>
  disabledReason?: string
  disabled?: boolean
  onAccept: (e: React.MouseEvent) => void
  display?: ComponentProps<typeof TokenPrice>["display"]
} & ButtonProps

export function AcceptOfferButton({
  makerAddress,
  pricePerItem,
  disabledReason,
  disabled,
  onAccept,
  display = "standard",
  size = "xs",
  ...rest
}: Props) {
  const address = useAddress()
  const isOwnProfileAddress = useIsOwnProfileAddress()

  const tokenPrice = <TokenPrice display={display} price={pricePerItem} />

  if (isAddressEqual(makerAddress, address) || !isOwnProfileAddress) {
    return tokenPrice
  }

  return (
    <Tooltip content={disabledReason} disabled={!disabled}>
      <span className="max-w-full">
        <Button
          className="max-w-full font-normal"
          disabled={disabled}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            onAccept(e)
          }}
          size={size}
          variant="secondary"
          {...rest}
        >
          {tokenPrice}
        </Button>
      </span>
    </Tooltip>
  )
}
